/**
 * 통합관리자 — 시스템 상태
 * ---------------------------------------------------------------------------
 * "지금 서버가 괜찮은가" 를 한 화면에서 본다.
 *
 * 수치를 어디서 잰 것인지 꼭 적는다
 *   CPU·메모리는 호스트 전체 값이고, 네트워크는 (호스트 카운터를 붙여 주지
 *   않았다면) API 컨테이너 값이다. 섞어 놓으면 "메모리는 32G 인데 네트워크는
 *   왜 이것뿐이지" 같은 오해가 생긴다.
 *
 * 자동 새로고침
 *   기본 10초. 끌 수 있게 둔다 — 화면을 띄워 놓고 다른 일을 할 때 계속 요청이
 *   나가면 로그가 지저분해진다.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Card,
  Col,
  Descriptions,
  Progress,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

/* =========================================================================
 * 서버가 주는 모양
 * ====================================================================== */

interface Gauge {
  used: number;
  total: number | null;
  percent: number | null;
}

interface Disk {
  label: string;
  path: string;
  usedBytes: number;
  totalBytes: number;
  percent: number;
}

interface Resources {
  measuredAt: string;
  hostname: string;
  cpu: {
    cores: number;
    percent: number;
    load1: number;
    load5: number;
    load15: number;
    windowSeconds: number;
  };
  memory: Gauge;
  swap: Gauge | null;
  disks: Disk[];
  network: {
    scope: 'host' | 'container';
    windowSeconds: number;
    inBytesPerSec: number;
    outBytesPerSec: number;
    inBytesTotal: number;
    outBytesTotal: number;
  };
  hostUptimeSeconds: number;
  processUptimeSeconds: number;
  process: {
    heapUsedBytes: number;
    heapTotalBytes: number;
    rssBytes: number;
    eventLoopDelayMs: number;
    nodeVersion: string;
  };
}

interface ServiceCheck {
  key: string;
  label: string;
  ok: boolean;
  latencyMs: number | null;
  detail: string;
}

interface DatabaseStatus {
  databaseName: string;
  sizeBytes: number;
  connections: { active: number; idle: number; total: number; max: number };
  pool: { total: number; idle: number; waiting: number };
  largestTables: Array<{ name: string; bytes: number; rows: number }>;
  longestQuerySeconds: number | null;
}

/*
 * 기업별 사용량은 **여기 두지 않는다.**
 *
 * 예전에는 이 화면 맨 아래에 기업 목록이 또 있었다. 그런데 「고객 › 기업별
 * 사용량」이 같은 자료를 더 자세히(제품별·지표별) 보여 준다. 같은 숫자를 두
 * 화면이 각자 그리면 한쪽만 고쳐지는 날이 오고, 둘이 다르면 어느 쪽이 맞는지
 * 알 수 없다. 이 화면은 **서버 상태**만 본다.
 */

/* =========================================================================
 * 화면
 * ====================================================================== */

/** 몇 초마다 다시 볼 것인가. 짧게 두면 CPU 표본 구간도 짧아져 수치가 튄다. */
const REFRESH_MS = 10_000;

export default function SystemStatusPage() {
  const [live, setLive] = useState(true);

  const resources = useQuery<Resources, NormalizedError>({
    queryKey: ['admin', 'system', 'resources'],
    queryFn: async () => (await apiClient.get<Resources>('/admin/system/resources')).data,
    refetchInterval: live ? REFRESH_MS : false,
  });

  const services = useQuery<ServiceCheck[], NormalizedError>({
    queryKey: ['admin', 'system', 'services'],
    queryFn: async () => (await apiClient.get<ServiceCheck[]>('/admin/system/services')).data,
    refetchInterval: live ? REFRESH_MS * 3 : false,
  });

  const database = useQuery<DatabaseStatus, NormalizedError>({
    queryKey: ['admin', 'system', 'database'],
    queryFn: async () => (await apiClient.get<DatabaseStatus>('/admin/system/database')).data,
    refetchInterval: live ? REFRESH_MS * 3 : false,
  });

  // 화면을 떠나면 자동 새로고침을 끈다. 탭을 여러 개 띄워 두는 일이 잦다.
  useEffect(() => () => setLive(false), []);

  const r = resources.data;

  // 막대의 기준. 서버가 큰 순서로 주지만, 그 가정에 기대지 않는다 —
  // 정렬이 바뀌면 막대가 전부 100% 로 보인다.
  const largestTableBytes = Math.max(
    1,
    ...(database.data?.largestTables ?? []).map((t) => t.bytes),
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {resources.isError && <Alert type="error" showIcon message={errorText(resources.error)} />}

      <Card size="small">
        <Space wrap size="large">
          <Space size="small">
            <Switch checked={live} onChange={setLive} size="small" />
            <Text type="secondary">{REFRESH_MS / 1000}초마다 자동 새로고침</Text>
          </Space>
          {r && (
            <>
              <Text type="secondary">
                서버 <Text code>{r.hostname}</Text>
              </Text>
              <Text type="secondary">가동 {formatDuration(r.hostUptimeSeconds)}</Text>
              <Text type="secondary">
                API 프로세스 {formatDuration(r.processUptimeSeconds)} · Node {r.process.nodeVersion}
              </Text>
              <Text type="secondary">측정 {new Date(r.measuredAt).toLocaleTimeString('ko-KR')}</Text>
            </>
          )}
        </Space>
      </Card>

      {/* ---- 자원 ---------------------------------------------------- */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card size="small" title="CPU" loading={resources.isLoading}>
            <Progress
              type="dashboard"
              size={120}
              percent={r?.cpu.percent ?? 0}
              strokeColor={levelColor(r?.cpu.percent ?? 0)}
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              코어 {r?.cpu.cores ?? '-'}개 · 최근 {r?.cpu.windowSeconds ?? 0}초 평균
              <br />
              부하 {r?.cpu.load1 ?? '-'} / {r?.cpu.load5 ?? '-'} / {r?.cpu.load15 ?? '-'}
              <Tooltip title="1분 / 5분 / 15분 평균 대기 작업 수. 코어 수보다 꾸준히 크면 밀리고 있는 것입니다.">
                <Text type="secondary"> (?)</Text>
              </Tooltip>
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <Card size="small" title="메모리" loading={resources.isLoading}>
            <Progress
              type="dashboard"
              size={120}
              percent={r?.memory.percent ?? 0}
              strokeColor={levelColor(r?.memory.percent ?? 0)}
            />
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              {formatBytes(r?.memory.used ?? 0)} / {formatBytes(r?.memory.total ?? 0)}
              <br />
              {r?.swap
                ? `스왑 ${formatBytes(r.swap.used)} / ${formatBytes(r.swap.total ?? 0)}`
                : '스왑 없음'}
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <Card size="small" title="디스크" loading={resources.isLoading}>
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {(r?.disks ?? []).map((d) => (
                <div key={d.path}>
                  <Text style={{ fontSize: 12 }}>
                    {d.label} <Text type="secondary">({d.path})</Text>
                  </Text>
                  <Progress
                    percent={d.percent}
                    size="small"
                    strokeColor={levelColor(d.percent)}
                    format={(p) => `${p}%`}
                  />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {formatBytes(d.usedBytes)} / {formatBytes(d.totalBytes)}
                  </Text>
                </div>
              ))}
              {(r?.disks ?? []).length === 0 && !resources.isLoading && (
                <Text type="secondary">읽지 못했습니다.</Text>
              )}
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={12} xl={6}>
          <Card
            size="small"
            title="네트워크"
            loading={resources.isLoading}
            extra={
              r && (
                <Tag color={r.network.scope === 'host' ? 'blue' : 'default'}>
                  {r.network.scope === 'host' ? '서버 전체' : 'API 컨테이너'}
                </Tag>
              )
            }
          >
            <Row gutter={8}>
              <Col span={12}>
                <Statistic
                  title="수신"
                  value={formatRate(r?.network.inBytesPerSec ?? 0)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="송신"
                  value={formatRate(r?.network.outBytesPerSec ?? 0)}
                  valueStyle={{ fontSize: 18 }}
                />
              </Col>
            </Row>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
              누적 ↓{formatBytes(r?.network.inBytesTotal ?? 0)} ↑
              {formatBytes(r?.network.outBytesTotal ?? 0)}
              <br />
              최근 {r?.network.windowSeconds ?? 0}초 평균
            </Paragraph>
            {r?.network.scope === 'container' && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                서버 전체 값을 보려면 compose 에 <Text code>/proc/net/dev:/host/net/dev:ro</Text> 를
                붙이세요.
              </Text>
            )}
          </Card>
        </Col>
      </Row>

      {/* ---- 의존 서비스 ---------------------------------------------- */}
      <Card size="small" title="서비스 점검" loading={services.isLoading}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {(services.data ?? []).map((s) => (
            <Space key={s.key} size="middle" wrap>
              <Badge status={s.ok ? 'success' : 'error'} text={s.label} />
              {s.latencyMs !== null && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {s.latencyMs}ms
                </Text>
              )}
              <Text type={s.ok ? 'secondary' : 'danger'} style={{ fontSize: 12 }}>
                {s.detail}
              </Text>
            </Space>
          ))}
          {services.isError && <Alert type="error" showIcon message={errorText(services.error)} />}
        </Space>
      </Card>

      {/* ---- 프로세스 / DB -------------------------------------------- */}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={12}>
          <Card size="small" title="API 프로세스" loading={resources.isLoading}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="힙 사용">
                {formatBytes(r?.process.heapUsedBytes ?? 0)} /{' '}
                {formatBytes(r?.process.heapTotalBytes ?? 0)}
              </Descriptions.Item>
              <Descriptions.Item label="RSS">
                {formatBytes(r?.process.rssBytes ?? 0)}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <Tooltip title="이벤트 루프가 밀린 시간. 수십 ms 를 넘으면 무언가 오래 붙잡고 있는 것입니다.">
                    이벤트 루프 지연
                  </Tooltip>
                }
              >
                <Text type={(r?.process.eventLoopDelayMs ?? 0) > 50 ? 'danger' : undefined}>
                  {r?.process.eventLoopDelayMs ?? 0}ms
                </Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card size="small" title="데이터베이스" loading={database.isLoading}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="데이터베이스">
                {database.data?.databaseName ?? '-'} · {formatBytes(database.data?.sizeBytes ?? 0)}
              </Descriptions.Item>
              {/*
                연결은 **한도가 있는 비율**이라 숫자보다 미터가 맞다.
                연결이 다 차면 새 요청이 줄을 서다 타임아웃으로 떨어지는데,
                "80 / 100" 만 적혀 있으면 위험한 줄 모르고 지나간다.
              */}
              <Descriptions.Item label="연결">
                <ConnectionMeter
                  total={database.data?.connections.total ?? 0}
                  max={database.data?.connections.max ?? 0}
                  active={database.data?.connections.active ?? 0}
                  idle={database.data?.connections.idle ?? 0}
                />
              </Descriptions.Item>
              <Descriptions.Item label="API 연결 풀">
                보유 {database.data?.pool.total ?? 0} · 유휴 {database.data?.pool.idle ?? 0} ·
                기다리는 요청 {database.data?.pool.waiting ?? 0}
              </Descriptions.Item>
              <Descriptions.Item label="가장 오래 도는 질의">
                {database.data?.longestQuerySeconds !== null &&
                database.data?.longestQuerySeconds !== undefined
                  ? `${database.data.longestQuerySeconds}초`
                  : '없음'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card size="small" title="큰 표 (상위 10개)" loading={database.isLoading}>
        {/*
          막대는 **가장 큰 표를 기준으로 한 상대 크기**다.
          숫자만 있으면 "5MB 와 1.4MB 중 어느 쪽이 문제인가" 가 한눈에 안 들어온다.
          길이로 크기를 나타내므로 색은 한 가지만 쓴다 — 막대마다 색을 바꾸면
          색이 아무 뜻도 없으면서 눈만 끈다.
        */}
        <Table
          rowKey="name"
          size="small"
          pagination={false}
          dataSource={database.data?.largestTables ?? []}
          columns={
            [
              { title: '표', dataIndex: 'name', key: 'name', width: 220, ellipsis: true },
              {
                title: '크기',
                dataIndex: 'bytes',
                key: 'bytes',
                render: (v: number) => (
                  <SizeBar value={v} max={largestTableBytes} label={formatBytes(v)} />
                ),
              },
              {
                title: '행 수(추정)',
                dataIndex: 'rows',
                key: 'rows',
                width: 140,
                align: 'right',
                render: (v: number) => (
                  <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {v.toLocaleString('ko-KR')}
                  </Text>
                ),
              },
            ] as ColumnsType<{ name: string; bytes: number; rows: number }>
          }
        />
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
          막대 길이는 가장 큰 표를 100%로 본 상대 크기입니다. 행 수는 통계에서 읽은 추정치입니다 —
          정확한 수를 세려면 표마다 전체를 훑어야 해서, 화면을 열 때마다 그러지 않습니다.
        </Paragraph>
      </Card>

    </Space>
  );
}

/* =========================================================================
 * 조각
 * ====================================================================== */

/**
 * 가로 막대 하나.
 *
 * 길이가 크기를 나타내므로 **색은 한 가지**만 쓴다. 막대마다 색을 바꾸면
 * 색이 아무 뜻도 없으면서 눈만 끈다. 끝을 둥글게 깎아 축에서 자란 모양으로
 * 두고, 숫자는 막대 옆에 직접 붙인다 — 축을 따로 그리면 표 안에서 자리를
 * 너무 먹는다.
 *
 * 색은 antd 테마의 기본색을 쓴다. 밝은/어두운 화면이 알아서 갈린다.
 */
function SizeBar({ value, max, label }: { value: number; max: number; label: string }) {
  const { token } = theme.useToken();
  // 아주 작은 표도 보이게 최소 2% 는 그린다. 0 으로 보이면 "값이 없나?" 가 된다.
  const percent = Math.max(2, Math.min(100, (value / max) * 100));

  return (
    <Tooltip title={`${label} · 가장 큰 표의 ${Math.round((value / max) * 100)}%`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          aria-hidden
          style={{
            flex: 1,
            minWidth: 60,
            height: 10,
            borderRadius: 4,
            background: token.colorFillSecondary,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${percent}%`,
              height: '100%',
              borderRadius: 4,
              background: token.colorPrimary,
            }}
          />
        </div>
        <Text style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{label}</Text>
      </div>
    </Tooltip>
  );
}

/**
 * 연결 사용률.
 *
 * 색만으로 알리지 않는다 — 글자로도 「여유 · 주의 · 위험」을 적는다.
 * 색을 못 가리는 사람에게도, 흑백으로 인쇄해도 뜻이 남아야 한다.
 */
function ConnectionMeter({
  total,
  max,
  active,
  idle,
}: {
  total: number;
  max: number;
  active: number;
  idle: number;
}) {
  if (max <= 0) {
    return (
      <Text type="secondary">
        사용 중 {active} · 대기 {idle} · 합계 {total}
      </Text>
    );
  }

  const percent = Math.round((total / max) * 100);
  // 경계는 운영 경험에서 나온 값이다. 80% 를 넘으면 순간 몰림에 바로 찬다.
  const level = percent >= 90 ? 'danger' : percent >= 80 ? 'warning' : 'ok';
  const label = level === 'danger' ? '위험' : level === 'warning' ? '주의' : '여유';

  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Space size={8}>
        <Text style={{ fontVariantNumeric: 'tabular-nums' }}>
          {total} / {max}
        </Text>
        <Tag color={level === 'danger' ? 'red' : level === 'warning' ? 'orange' : 'default'}>
          {label} {percent}%
        </Tag>
      </Space>
      <Progress
        percent={percent}
        size="small"
        showInfo={false}
        status={level === 'danger' ? 'exception' : level === 'warning' ? 'active' : 'normal'}
      />
      <Text type="secondary" style={{ fontSize: 12 }}>
        사용 중 {active} · 대기 {idle}
      </Text>
    </Space>
  );
}

/*
 * Quota 는 기업별 사용량 표와 함께 걷어냈다. 같은 모양이 필요하면
 * 「고객 › 기업별 사용량」 화면의 것을 쓴다 — 거기가 그 자료의 집이다.
 */

/** 70% 노랑, 90% 빨강. 눈으로 바로 구분되어야 한다. */
function levelColor(percent: number): string {
  if (percent >= 90) return '#cf1322';
  if (percent >= 70) return '#d48806';
  return '#3f8600';
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)}${units[i]}`;
}

function formatRate(bytesPerSec: number): string {
  return `${formatBytes(bytesPerSec)}/s`;
}

function formatDuration(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}일 ${h}시간`;
  if (h > 0) return `${h}시간 ${m}분`;
  return `${m}분`;
}
